import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getActivePO } from '../lib/storeMode';
import { getActiveHoliday } from '../lib/holiday';
import { withTimeout } from '../lib/withTimeout';
import { getDeliverySettings, getCurrentPosition, haversineKm } from '../lib/geo';
import { verifyCartStock, decrementStockForOrder } from '../lib/stock';
import { localDateStr } from '../lib/date';
import { sendDirectOrderConfirmWA, sendPOSOrderConfirmWA, notifyKitchenNewOrder } from '../lib/fonnte';
import ClosedScreen from '../components/ClosedScreen';
import StoreNav from '../components/StoreNav';
import { useCart } from '../context/CartContext';

function todayStr() {
  return localDateStr();
}

function fmtDateShort(d) {
  return new Date(d).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

// Jam default: sekarang + 30 menit, dibulatkan ke kelipatan 5 menit terdekat
function defaultTime() {
  const d = new Date(Date.now() + 30 * 60000);
  const mins = Math.ceil(d.getMinutes() / 5) * 5;
  d.setMinutes(mins >= 60 ? 0 : mins);
  if (mins >= 60) d.setHours(d.getHours() + 1);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function genPosOrderId() {
  const ymd = todayStr().replace(/-/g, '');
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `WEB-${ymd}-${rand}`;
}

export default function Cart() {
  const nav = useNavigate();
  const { items, setQty, removeItem, totalPrice, totalQty, clearCart } = useCart();
  const kind = items[0]?.kind === 'pos' ? 'pos' : 'po'; // default 'po' untuk kompatibilitas item lama di localStorage

  const [fulfillType, setFulfillType] = useState('pickup'); // 'pickup' | 'delivery'
  const [fulfillDate, setFulfillDate] = useState(todayStr());
  const [fulfillTime, setFulfillTime] = useState(defaultTime());
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [form, setForm] = useState({ name: '', wa: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [poBatch, setPoBatch] = useState(null);
  const [poBatchLoading, setPoBatchLoading] = useState(kind === 'po');
  const [holiday, setHoliday] = useState(undefined);

  useEffect(() => {
    withTimeout(getActiveHoliday(), 6000, null).then(setHoliday);
  }, []);

  // Cek jarak delivery: 'idle' | 'checking' | 'ok' | 'toofar' | 'error' | 'unset'
  const [geoStatus, setGeoStatus] = useState('idle');
  const [geoDistanceKm, setGeoDistanceKm] = useState(null);
  const [deliverySettings, setDeliverySettings] = useState({ lat: null, lng: null, radiusKm: 3 });

  useEffect(() => {
    if (kind !== 'pos') return;
    getDeliverySettings().then(setDeliverySettings);
  }, [kind]);

  // Reset hasil cek jarak setiap kali ganti metode / ubah alamat
  useEffect(() => {
    setGeoStatus('idle');
    setGeoDistanceKm(null);
  }, [fulfillType]);

  async function checkDeliveryDistance() {
    if (!deliverySettings.lat || !deliverySettings.lng) {
      setGeoStatus('unset');
      return;
    }
    setGeoStatus('checking');
    try {
      const { lat, lng } = await getCurrentPosition();
      const km = haversineKm(lat, lng, deliverySettings.lat, deliverySettings.lng);
      setGeoDistanceKm(km);
      setGeoStatus(km <= deliverySettings.radiusKm ? 'ok' : 'toofar');
    } catch (e) {
      console.warn(e);
      setGeoStatus('error');
    }
  }

  useEffect(() => {
    if (kind !== 'po') return;
    let active = true;
    withTimeout(getActivePO(), 6000, null).then(data => {
      if (!active) return;
      setPoBatch(data);
      if (data?.date) setFulfillDate(data.date);
      setPoBatchLoading(false);
    });
    return () => { active = false; };
    // eslint-disable-next-line
  }, []);

  async function upsertCustomer(wa, name) {
    const { data: existing } = await supabase.from('customers').select('id').eq('wa', wa).maybeSingle();
    if (existing) {
      await supabase.from('customers').update({
        name,
        address: fulfillType === 'delivery' ? address.trim() : undefined,
        source: 'both',
      }).eq('id', existing.id);
      return { id: existing.id, isNew: false };
    }
    const { data: newC } = await supabase.from('customers')
      .insert({ name, wa, address: fulfillType === 'delivery' ? address.trim() : '', source: kind === 'pos' ? 'pos' : 'po' })
      .select('id').single();
    return { id: newC.id, isNew: true };
  }

  async function submitPOOrder(wa) {
    const refCode = localStorage.getItem('sapan_ref') || null;
    let refStaff = null;
    if (refCode) {
      const { data } = await supabase.from('users').select('id, name').eq('ref_code', refCode).eq('is_active', true).maybeSingle();
      refStaff = data || null;
    }

    const { id: customerId } = await upsertCustomer(wa, form.name.trim());
    const poFulfillDate = poBatch?.date || fulfillDate;
    const meetPointNote = poBatch?.area?.name ? `Ambil di: ${poBatch.area.name}` : '';

    const { data: order, error: orderErr } = await supabase
      .from('po_orders')
      .insert({
        po_id: poBatch?.id || null,
        customer_id: customerId,
        qty: totalQty,
        total: totalPrice,
        payment_method: 'cod',
        payment_status: 'unpaid',
        status: 'pending',
        fulfillment_type: 'pickup',
        fulfillment_date: poFulfillDate,
        delivery_address: '',
        notes: [meetPointNote, notes.trim()].filter(Boolean).join(' · '),
        ref_code: refCode || null,
        referred_by_user_id: refStaff?.id || null,
        variant_name: '', variant_price: 0,
      })
      .select().single();
    if (orderErr) throw orderErr;

    await supabase.from('po_order_items').insert(
      items.map(i => ({
        po_order_id: order.id,
        variant_id: i.variant_id,
        variant_name: i.variant_name || i.product_name,
        variant_price: i.price,
        qty: i.qty,
        subtotal: i.price * i.qty,
      }))
    );

    sendDirectOrderConfirmWA(wa, order.id, form.name.trim(), items, totalPrice, 'pickup', poFulfillDate, poBatch?.area?.name || '').catch(console.warn);

    clearCart();
    nav(`/order/${order.id}`);
    return true;
  }

  async function submitPOSOrder(wa) {
    const { id: customerId } = await upsertCustomer(wa, form.name.trim());
    const orderId = genPosOrderId();
    const jamNote = `Jam ${fulfillType === 'delivery' ? 'diantar' : 'diambil'}: ${fulfillTime}`;
    const jarakNote = fulfillType === 'delivery' && geoDistanceKm != null ? `Jarak dari outlet: ~${geoDistanceKm.toFixed(1)}km` : '';

    const { error: orderErr } = await supabase.from('pos_orders').insert({
      id: orderId,
      type: fulfillType === 'delivery' ? 'Delivery' : 'Take Away',
      customer_name: form.name.trim(),
      payment: 'Cash',
      status: 'New',
      total: totalPrice,
      original_total: totalPrice,
      discount_amount: 0,
      customer_id: customerId,
      delivery_phone: wa,
      delivery_address: fulfillType === 'delivery' ? address.trim() : '',
      notes: [jamNote, jarakNote, notes.trim()].filter(Boolean).join(' · '),
      source: 'web',
    });
    if (orderErr) throw orderErr;

    await supabase.from('order_items').insert(
      items.map(i => {
        const displayName = i.variant_name ? `${i.product_name} – ${i.variant_name}` : i.product_name;
        const marginAmount = (i.price - (i.hpp || 0)) * i.qty;
        return {
          order_id: orderId,
          item_id: i.product_id,
          display_name: displayName,
          note: i.note || '',
          unit_price: i.price,
          qty: i.qty,
          hpp_snapshot: i.hpp || 0,
          margin_amount: marginAmount,
          margin_pct: i.price ? Math.round((marginAmount / (i.price * i.qty)) * 1000) / 10 : 0,
        };
      })
    );

    // Kirim WA konfirmasi ke customer + notifikasi order baru ke staf dapur (non-blocking)
    sendPOSOrderConfirmWA(wa, orderId, form.name.trim(), items, totalPrice, fulfillType, address.trim(), fulfillTime).catch(console.warn);
    notifyKitchenNewOrder(orderId, form.name.trim(), items, totalPrice, fulfillType, address.trim(), fulfillTime).catch(console.warn);

    // Kurangi stok untuk item yang dilacak stoknya (best-effort, tidak menggagalkan order kalau error)
    decrementStockForOrder(items, orderId).catch(console.warn);

    clearCart();
    nav(`/order/${orderId}`);
    return true;
  }

  async function handleSubmit() {
    if (items.length === 0) { setError('Keranjang masih kosong'); return; }
    if (holiday) { setError('Mohon maaf, kami sedang libur dan belum bisa menerima pesanan hari ini.'); return; }
    if (!form.name.trim() || !form.wa.trim()) { setError('Nama dan nomor WhatsApp wajib diisi'); return; }
    if (kind === 'pos' && !fulfillTime) { setError('Pilih jam pengambilan/pengantaran'); return; }
    if (kind === 'pos' && fulfillType === 'delivery') {
      if (!address.trim()) { setError('Alamat pengiriman wajib diisi'); return; }
      if (geoStatus !== 'ok' && geoStatus !== 'unset') {
        setError('Klik "Cek Jarak dari Outlet" dulu ya sebelum checkout, supaya kami pastikan alamat kamu masih bisa dijangkau delivery.');
        return;
      }
      if (geoStatus === 'toofar') { setError(`Alamat kamu di luar radius delivery kami (maks ${deliverySettings.radiusKm}km). Silakan pilih Ambil Sendiri.`); return; }
    }
    setLoading(true); setError('');

    try {
      // Cek stok terkini sebelum submit — jaga-jaga stok berubah sejak item dimasukkan keranjang
      if (kind === 'pos') {
        const { ok, problems } = await verifyCartStock(items);
        if (!ok) {
          const detail = problems.map(p => `${p.name} (sisa ${p.available})`).join(', ');
          setError(`Maaf, stok beberapa item sudah berubah: ${detail}. Silakan sesuaikan jumlah di keranjang ya.`);
          setLoading(false);
          return;
        }
      }

      const wa = form.wa.trim();
      if (kind === 'pos') await submitPOSOrder(wa);
      else await submitPOOrder(wa);
    } catch (e) {
      console.error(e);
      setError('Terjadi kesalahan: ' + (e?.message || 'coba lagi'));
    }
    setLoading(false);
  }

  if (holiday) {
    return <ClosedScreen holiday={holiday} />;
  }

  return (
    <div className="store" style={{ minHeight: '100vh', paddingBottom: 40 }}>
      <StoreNav />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 20px 60px' }} className="fade-up">
        <h1 style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.9rem', color: 'var(--ink)', marginBottom: 22 }}>Keranjang Kamu</h1>

        {items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '56px 24px', background: 'var(--sand)', borderRadius: 'var(--store-radius)' }}>
            <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.2rem', marginBottom: 8 }}>Keranjang masih kosong</div>
            <button onClick={() => nav('/')} style={{ marginTop: 8, background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 999, padding: '11px 22px', fontSize: '.84rem', fontWeight: 700 }}>
              Lihat Produk
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {items.map(i => (
                <div key={i.key} style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 14, padding: 12 }}>
                  <div style={{ width: 56, height: 56, borderRadius: 10, flexShrink: 0, background: i.image_url ? undefined : (i.swatch || 'var(--sand)'), overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' }}>
                    {i.image_url ? <img src={i.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (i.emoji || null)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '.88rem', color: 'var(--ink)' }}>{i.product_name}</div>
                    {i.variant_name && <div style={{ fontSize: '.74rem', color: 'var(--ink-soft)' }}>{i.variant_name}</div>}
                    {i.note && <div style={{ fontSize: '.72rem', color: 'var(--ink-soft)' }}>{i.note}</div>}
                    <div style={{ fontSize: '.8rem', color: 'var(--primary)', marginTop: 2 }}>Rp {i.price.toLocaleString('id-ID')}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button onClick={() => setQty(i.key, i.qty - 1)} style={{ width: 26, height: 26, borderRadius: '50%', border: '1px solid var(--line)', background: 'var(--sand)' }}>−</button>
                    <span style={{ minWidth: 18, textAlign: 'center', fontSize: '.86rem' }}>{i.qty}</span>
                    <button onClick={() => setQty(i.key, i.qty + 1)} style={{ width: 26, height: 26, borderRadius: '50%', border: '1px solid var(--line)', background: 'var(--sand)' }}>+</button>
                  </div>
                  <button onClick={() => removeItem(i.key)} style={{ background: 'transparent', border: 'none', color: 'var(--ink-soft)', fontSize: '.78rem', padding: '0 4px' }}>✕</button>
                </div>
              ))}
            </div>

            <div style={{ background: 'var(--sand)', borderRadius: 12, padding: '12px 16px', marginBottom: 28, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '.86rem', color: 'var(--ink-soft)' }}>Total ({totalQty} item)</span>
              <span style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.2rem', color: 'var(--primary)' }}>Rp {totalPrice.toLocaleString('id-ID')}</span>
            </div>

            {/* Fulfillment */}
            <div style={{ marginBottom: 24 }}>
              {kind === 'po' ? (
                <>
                  <div style={{ fontSize: '.76rem', color: 'var(--ink-soft)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Info Pengambilan</div>
                  {poBatchLoading ? (
                    <div style={{ background: 'var(--sand)', borderRadius: 12, padding: '14px 16px', fontSize: '.82rem', color: 'var(--ink-soft)', marginBottom: 14 }}>
                      Memuat info PO...
                    </div>
                  ) : (
                    <div style={{ background: 'var(--sand)', borderRadius: 12, padding: '14px 16px', marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.84rem', color: 'var(--ink)' }}>
                        <span>📍</span>
                        <span><strong>Lokasi:</strong> {poBatch?.area?.name || 'Info menyusul, kami hubungi via WhatsApp'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.84rem', color: 'var(--ink)' }}>
                        <span>📅</span>
                        <span><strong>Tanggal:</strong> {poBatch?.date ? fmtDateShort(poBatch.date) : '-'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.84rem', color: 'var(--ink)' }}>
                        <span>⏰</span>
                        <span><strong>Jam:</strong> {poBatch?.time_start && poBatch?.time_end ? `${poBatch.time_start.slice(0, 5)} – ${poBatch.time_end.slice(0, 5)}` : '-'}</span>
                      </div>
                      <div style={{ fontSize: '.72rem', color: 'var(--ink-soft)', marginTop: 2 }}>
                        Pesanan PO diambil sendiri di lokasi &amp; jadwal di atas ya, bukan diantar ke alamat masing-masing.
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{ fontSize: '.76rem', color: 'var(--ink-soft)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Cara Terima Pesanan</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                    {[['pickup', 'Ambil Sendiri', 'Ambil langsung di lokasi kami'], ['delivery', 'Diantar', 'Kami antar ke alamatmu']].map(([val, label, desc]) => (
                      <button key={val} type="button" onClick={() => setFulfillType(val)} style={{
                        padding: '13px 14px', borderRadius: 12, textAlign: 'left',
                        border: fulfillType === val ? '2px solid var(--primary)' : '2px solid var(--line)',
                        background: fulfillType === val ? 'var(--primary-soft)' : 'var(--paper)',
                      }}>
                        <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--ink)' }}>{label}</div>
                        <div style={{ fontSize: '.72rem', color: 'var(--ink-soft)', marginTop: 2 }}>{desc}</div>
                      </button>
                    ))}
                  </div>

                  {fulfillType === 'delivery' && (
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: '.76rem', color: 'var(--ink-soft)', marginBottom: 5 }}>Alamat Lengkap</label>
                      <textarea rows={2} placeholder="Nama jalan, nomor rumah, patokan..." value={address} onChange={e => setAddress(e.target.value)}
                        style={{ width: '100%', border: '1.5px solid var(--line)', borderRadius: 10, padding: '10px 13px', fontSize: '.86rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />

                      <div style={{ marginTop: 10 }}>
                        <button type="button" onClick={checkDeliveryDistance} disabled={geoStatus === 'checking'} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 999,
                          border: '1.5px solid var(--primary)', background: 'var(--primary-soft)', color: 'var(--primary-hover)',
                          fontSize: '.8rem', fontWeight: 700, cursor: geoStatus === 'checking' ? 'default' : 'pointer',
                        }}>
                          📍 {geoStatus === 'checking' ? 'Mengecek lokasi...' : 'Cek Jarak dari Outlet'}
                        </button>
                        <div style={{ fontSize: '.7rem', color: 'var(--ink-soft)', marginTop: 6 }}>
                          Kami cek jarak dari lokasi HP kamu sekarang — pastikan kamu sedang di lokasi pengantaran ya.
                        </div>

                        {geoStatus === 'ok' && (
                          <div style={{ marginTop: 8, background: '#E8F5E9', color: '#1B5E20', borderRadius: 8, padding: '9px 13px', fontSize: '.78rem' }}>
                            ✅ Sekitar {geoDistanceKm.toFixed(1)}km dari outlet, masih bisa diantar.
                          </div>
                        )}
                        {geoStatus === 'toofar' && (
                          <div style={{ marginTop: 8, background: '#FDEBE5', color: '#B23A15', borderRadius: 8, padding: '9px 13px', fontSize: '.78rem' }}>
                            ❌ Sekitar {geoDistanceKm.toFixed(1)}km dari outlet — di luar radius delivery kami (maks {deliverySettings.radiusKm}km). Coba pilih Ambil Sendiri ya.
                          </div>
                        )}
                        {geoStatus === 'error' && (
                          <div style={{ marginTop: 8, background: '#FFF3E0', color: '#B26A00', borderRadius: 8, padding: '9px 13px', fontSize: '.78rem' }}>
                            Nggak bisa akses lokasi kamu. Izinkan akses lokasi di browser, lalu coba lagi.
                          </div>
                        )}
                        {geoStatus === 'unset' && (
                          <div style={{ marginTop: 8, background: '#FFF3E0', color: '#B26A00', borderRadius: 8, padding: '9px 13px', fontSize: '.78rem' }}>
                            Lokasi outlet belum diatur admin, pengecekan jarak dilewati — kami akan konfirmasi manual via WhatsApp.
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: '.76rem', color: 'var(--ink-soft)', marginBottom: 5 }}>
                      Jam {fulfillType === 'delivery' ? 'Diantar' : 'Diambil'}
                    </label>
                    <input type="time" value={fulfillTime} onChange={e => setFulfillTime(e.target.value)}
                      style={{ width: '100%', border: '1.5px solid var(--line)', borderRadius: 10, padding: '10px 13px', fontSize: '.86rem', outline: 'none', boxSizing: 'border-box' }} />
                  </div>

                  <div style={{ background: 'var(--sand)', borderRadius: 10, padding: '10px 13px', fontSize: '.78rem', color: 'var(--ink-soft)', marginBottom: 14 }}>
                    Pesanan langsung disiapkan dapur kami hari ini, bukan pre-order.
                  </div>
                </>
              )}

              <div>
                <label style={{ display: 'block', fontSize: '.76rem', color: 'var(--ink-soft)', marginBottom: 5 }}>Catatan <span style={{ color: '#AAA' }}>(opsional)</span></label>
                <input type="text" placeholder="Contoh: titip ke satpam, hubungi dulu sebelum sampai" value={notes} onChange={e => setNotes(e.target.value)}
                  style={{ width: '100%', border: '1.5px solid var(--line)', borderRadius: 10, padding: '10px 13px', fontSize: '.86rem', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ height: 1, background: 'var(--line)', marginBottom: 24 }} />

            {/* Data pemesan */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: '.76rem', color: 'var(--ink-soft)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Data Pemesan</div>
              {[['Nama Lengkap', 'name', 'text', 'Nama kamu'], ['Nomor WhatsApp', 'wa', 'tel', '08xxxxxxxxxx']].map(([label, field, type, ph]) => (
                <div key={field} style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: '.76rem', color: 'var(--ink-soft)', marginBottom: 5 }}>{label}</label>
                  <input type={type} placeholder={ph} value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                    style={{ width: '100%', border: '1.5px solid var(--line)', borderRadius: 10, padding: '10px 13px', fontSize: '.86rem', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>

            {/* Payment info — COD only */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', background: 'var(--sand)', borderRadius: 12, marginBottom: 20 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--primary-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--primary)', fontWeight: 700, fontSize: '.8rem' }}>Rp</div>
              <div>
                <div style={{ fontSize: '.84rem', fontWeight: 700, color: 'var(--ink)' }}>Bayar di Tempat (COD)</div>
                <div style={{ fontSize: '.74rem', color: 'var(--ink-soft)' }}>Saat ini kami hanya menerima pembayaran tunai saat pesanan diterima.</div>
              </div>
            </div>

            {error && (
              <div style={{ background: '#FDEBE5', borderRadius: 8, padding: '9px 13px', fontSize: '.78rem', color: '#B23A15', marginBottom: 14 }}>{error}</div>
            )}

            <button onClick={handleSubmit} disabled={loading} style={{
              width: '100%', padding: '15px', borderRadius: 999, border: 'none',
              background: loading ? '#D9BBAA' : 'var(--primary)', color: '#fff', fontSize: '.94rem', fontWeight: 700,
            }}>
              {loading ? 'Memproses...' : `Buat Pesanan · Rp ${totalPrice.toLocaleString('id-ID')}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
